import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, Clock, Eye, Flag, Loader2, MessageSquareWarning, PenLine } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { formatDollars } from "../../lib/draftCampaign";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "");
const daysUntil = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
};

/**
 * ExtensionPanel — live campaigns: end date, days left, the one-time 15-day
 * extension request (admin-approved), and its outcome.
 */
export const ExtensionPanel = ({ draft, onRequest }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const days = daysUntil(draft.ends_at);
  const ext = draft.extension_request;
  const status = ext?.status;

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 10) { toast.error("Tell us a little more about why you need the extra time."); return; }
    setBusy(true);
    try {
      await onRequest(trimmed);
      toast.success("Extension requested.", { description: "A reviewer will look at it as soon as possible." });
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="extension-panel" data-state={status || "none"} className={`rounded-lg border p-5 ${draft.ending_soon && status !== "pending" ? "border-tangerine/50 bg-tangerine/[0.06]" : "border-border bg-bone"}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${draft.ending_soon ? "bg-tangerine text-sand" : "bg-sand text-ink ring-1 ring-inset ring-ink/10"}`}>
            <Clock className="h-4 w-4" strokeWidth={2} />
          </span>
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Campaign timeline</p>
            <p className="mt-1 font-serif text-3xl text-ink" data-testid="extension-days-left">
              {days > 0 ? <>{days} {days === 1 ? "day" : "days"} left</> : "Closing tonight"} <span className="text-base text-olive">· ends {fmtDate(draft.ends_at)}</span>
            </p>
            {draft.ending_soon && status !== "pending" && (
              <p className="mt-1 text-[13px] text-ink">The last week is where most campaigns close the gap. One more round of texts and stories does it.</p>
            )}
            {draft.extension_count > 0 && status === "approved" && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-[13px] text-grass" data-testid="extension-approved-note"><CheckCircle2 className="h-4 w-4" strokeWidth={2} /> Extended by {ext.days_added} days — this new end date is final.</p>
            )}
          </div>
        </div>
        {draft.can_request_extension && !open && (
          <button type="button" onClick={() => setOpen(true)} data-testid="extension-request-btn" className="inline-flex items-center gap-2 rounded-full border border-ink px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-ink hover:text-sand">
            <CalendarClock className="h-3.5 w-3.5" strokeWidth={2} /> Request a 15-day extension
          </button>
        )}
      </div>

      {status === "pending" && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-cobalt/30 bg-cobalt/[0.05] p-4" data-testid="extension-pending">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-cobalt" />
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-cobalt">Extension request in review</p>
            <p className="mt-1 text-[13px] text-ink">Sent {fmtDate(ext.requested_at)}. Your campaign stays open until a reviewer decides.</p>
            <blockquote className="mt-2 border-l-2 border-cobalt/40 pl-3 text-[13px] italic text-olive">&ldquo;{ext.reason}&rdquo;</blockquote>
          </div>
        </div>
      )}
      {status === "rejected" && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-scarlet/30 bg-scarlet/[0.05] p-4" data-testid="extension-rejected">
          <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0 text-scarlet" strokeWidth={2} />
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-scarlet">Extension not approved</p>
            <p className="mt-1 text-[13px] text-ink">Your campaign closes as planned on {fmtDate(draft.ends_at)}. Everything raised is yours.</p>
            {ext.rejection_reason && <blockquote className="mt-2 rounded-md border-l-4 border-scarlet bg-bone px-3 py-2 font-serif text-base text-ink">&ldquo;{ext.rejection_reason}&rdquo;</blockquote>}
          </div>
        </div>
      )}

      {open && (
        <div className="mt-4 rounded-md border border-border bg-sand p-4" data-testid="extension-form">
          <p className="text-[13px] font-semibold text-ink">Why do you need more time?</p>
          <p className="mt-0.5 text-[13px] text-olive">One extension per campaign, 15 days, approved by a reviewer. Be specific — it helps.</p>
          <Textarea data-testid="extension-reason" rows={3} maxLength={600} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. My host-family deposit moved to the 28th and I'm $140 short — two more weeks lets my church group's fundraiser land." className="mt-3" />
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={submit} disabled={busy} data-testid="extension-submit" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2 text-[13px] font-semibold text-sand hover:bg-forest disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" strokeWidth={2} />} Send request
            </button>
            <button type="button" onClick={() => setOpen(false)} data-testid="extension-cancel" className="rounded-full px-4 py-2 text-[13px] font-semibold text-olive hover:text-ink">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

/** ClosedPanel — the campaign has ended; the page stays up, editing is off. */
export const ClosedPanel = ({ draft, onStartNew }) => {
  const [starting, setStarting] = useState(false);
  const startNew = async () => {
    setStarting(true);
    try { await onStartNew(); toast.success("New draft started.", { description: "Your closed campaign stays up at its link." }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start a new campaign."); }
    finally { setStarting(false); }
  };
  const pct = draft.goal_cents ? Math.min(100, Math.round((draft.raised_cents / draft.goal_cents) * 100)) : 0;
  const hit = draft.goal_cents && draft.raised_cents >= draft.goal_cents;
  return (
    <div data-testid="builder-closed" className="overflow-hidden rounded-lg border border-border bg-bone">
      <div className="bg-ink px-8 py-8 text-sand">
        <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-yellow">Campaign closed · {fmtDate(draft.closed_at)}</p>
        <h3 className="mt-2 font-serif text-3xl">{hit ? "You did it." : "Thank you for showing up."}</h3>
        <p className="mt-2 max-w-xl text-[13px] text-sand/80">&ldquo;{draft.title}&rdquo; ended at {formatDollars(draft.raised_cents)} of {formatDollars(draft.goal_cents)} ({pct}%), backed by {draft.donor_count} supporter{draft.donor_count === 1 ? "" : "s"}. Grants keep going out on the weekly schedule until everything raised for you has been sent.</p>
      </div>
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        {[["Raised", formatDollars(draft.raised_cents)], ["Supporters", String(draft.donor_count)], ["Of goal", `${pct}%`]].map(([k, v]) => (
          <div key={k} className="px-6 py-5">
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">{k}</p>
            <p className="mt-1 font-serif text-3xl text-ink">{v}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 px-8 py-6">
        <Link to={`/campaign/${draft.id}`} target="_blank" rel="noopener noreferrer" data-testid="closed-view-page" className="inline-flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 text-base font-semibold text-ink hover:bg-ink hover:text-sand">
          <Eye className="h-4 w-4" strokeWidth={1.75} /> See your page
        </Link>
        <button type="button" onClick={startNew} disabled={starting} data-testid="closed-start-new" className="inline-flex items-center gap-2 rounded-full bg-yellow px-5 py-2.5 text-base font-semibold text-ink hover:bg-yellow-dark disabled:opacity-60">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" strokeWidth={1.75} />} Start a new campaign
        </button>
        <p className="inline-flex items-center gap-1.5 text-[13px] text-olive"><Flag className="h-4 w-4 text-tangerine" strokeWidth={2} /> When a grant lands, write a thank-you below — it pins to the top of your page.</p>
      </div>
    </div>
  );
};
